import React, { useState } from 'react';
import { DocumentStatusMatrixRow, STATUS_FIELDS, ThemeColors } from './types';

type DocumentStatusMatrixProps = {
    title: string;
    data: DocumentStatusMatrixRow[];
    themeColors: ThemeColors;
};

export const DocumentStatusMatrix: React.FC<DocumentStatusMatrixProps> = ({
    title,
    data,
    themeColors
}) => {
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    const toggleRow = (rowId: string) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(rowId)) {
                next.delete(rowId);
            } else {
                next.add(rowId);
            }
            return next;
        });
    };

    if (!data || data.length === 0) {
        return (
            <div style={{
                backgroundColor: themeColors.cardBackground,
                borderRadius: 12,
                padding: 24,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: `1px solid ${themeColors.cardBorder}`
            }}>
                <div style={{ textAlign: 'center', color: themeColors.textSecondary, padding: 40 }}>
                    No data available
                </div>
            </div>
        );
    }

    return (
        <div style={{
            backgroundColor: themeColors.cardBackground,
            borderRadius: 12,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: `1px solid ${themeColors.cardBorder}`
        }}>
            <div style={{
                fontSize: 15,
                fontWeight: 600,
                color: themeColors.text,
                marginBottom: 16
            }}>
                {title}
            </div>

            <div style={{ overflowX: 'auto' }}>
                <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 12,
                    color: themeColors.text
                }}>
                    <thead>
                        <tr>
                            <th style={{
                                textAlign: 'left',
                                padding: '10px 12px',
                                borderBottom: `1px solid ${themeColors.border}`,
                                fontWeight: 600,
                                whiteSpace: 'nowrap'
                            }}>
                                Documento
                            </th>
                            {STATUS_FIELDS.map(field => (
                                <th key={field.key} style={{
                                    textAlign: 'center',
                                    padding: '10px 12px',
                                    borderBottom: `1px solid ${themeColors.border}`,
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap'
                                }}>
                                    {field.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row) => {
                            const rowId = row.id || row.tipo_documento;
                            const hasChildren = (row.children || []).length > 0;
                            const isExpanded = hasChildren && expandedRows.has(rowId);
                            return (
                                <React.Fragment key={rowId}>
                                    <tr>
                                        <td style={{
                                            padding: '10px 12px',
                                            borderBottom: `1px solid ${themeColors.borderLight}`,
                                            fontWeight: 600,
                                            minWidth: 220
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8
                                            }}>
                                                {hasChildren ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleRow(rowId)}
                                                        style={{
                                                            width: 22,
                                                            height: 22,
                                                            borderRadius: 4,
                                                            border: `1px solid ${themeColors.border}`,
                                                            backgroundColor: themeColors.cardBackground,
                                                            color: themeColors.text,
                                                            fontSize: 12,
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        {isExpanded ? '-' : '+'}
                                                    </button>
                                                ) : (
                                                    <span style={{ width: 22, display: 'inline-block' }} />
                                                )}
                                                <span>{row.tipo_documento}</span>
                                            </div>
                                        </td>
                                        {STATUS_FIELDS.map(field => (
                                            <td key={field.key} style={{
                                                textAlign: 'center',
                                                padding: '10px 12px',
                                                borderBottom: `1px solid ${themeColors.borderLight}`,
                                                color: row[field.key] > 0 ? themeColors.text : themeColors.textSecondary,
                                                fontVariantNumeric: 'tabular-nums'
                                            }}>
                                                {row[field.key]}
                                            </td>
                                        ))}
                                    </tr>
                                    {isExpanded && (row.children || []).map((child) => (
                                        <tr key={child.id || `${rowId}:${child.tipo_documento}`}>
                                            <td style={{
                                                padding: '10px 12px 10px 44px',
                                                borderBottom: `1px solid ${themeColors.borderLight}`,
                                                fontWeight: 500,
                                                minWidth: 220,
                                                color: themeColors.textSecondary
                                            }}>
                                                {child.tipo_documento}
                                            </td>
                                            {STATUS_FIELDS.map(field => (
                                                <td key={field.key} style={{
                                                    textAlign: 'center',
                                                    padding: '10px 12px',
                                                    borderBottom: `1px solid ${themeColors.borderLight}`,
                                                    color: child[field.key] > 0 ? themeColors.text : themeColors.textSecondary,
                                                    fontVariantNumeric: 'tabular-nums'
                                                }}>
                                                    {child[field.key]}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
